/**
 * Agent Platform Login Page — /agent-platform/login
 *
 * Purpose: Handles the "interrupt flow" when an agent (e.g. in Slack) needs
 * to verify the user's identity.
 */

import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SafeTrans from "../../i18n/SafeTrans";
import {
  Container,
  Header,
  StatusIndicator,
  SpaceBetween,
  Box,
  Button,
} from "@amzn/awsui-components-react";
import { getIdToken } from "../../context/AuthContext";
import { authCallback } from "../../client/InternalApiClient";

type LoginState = "loading" | "resuming" | "success" | "error";

/** Error codes the Gateway returns, each with a message in settings:login.errors. */
const KNOWN_ERRORS = ["missing_state", "session_expired", "already_consumed", "unknown_channel", "store_unavailable", "auth_failed"] as const;
type LoginErrorKey = (typeof KNOWN_ERRORS)[number] | "noToken" | "noState" | "resumeFailed" | "unexpected";

interface LoginResult {
  userId?: string;
  // A server message is shown as-is; a key is translated at render.
  error?: { message: string } | { key: LoginErrorKey };
}

export default function AgentPlatformLogin() {
  const { t } = useTranslation("settings");
  const [searchParams] = useSearchParams();
  const stateParam = searchParams.get("state");
  const calledRef = useRef(false);
  const [state, setState] = useState<LoginState>("loading");
  const [result, setResult] = useState<LoginResult>({});

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const doLogin = async () => {
      try {
        const token = getIdToken();
        if (!token) {
          setState("error");
          setResult({ error: { key: "noToken" } });
          return;
        }

        if (!stateParam) {
          setState("error");
          setResult({ error: { key: "noState" } });
          return;
        }

        setState("resuming");

        const callbackResult = await authCallback(stateParam, token);

        if (callbackResult.statusCode === 200) {
          setState("success");
          setResult({ userId: callbackResult.body.alias || "" });
        } else {
          const errorCode = callbackResult.body.error ?? "";
          const known = (KNOWN_ERRORS as readonly string[]).includes(errorCode) ? (errorCode as LoginErrorKey) : null;
          const serverMessage = callbackResult.body.message;
          setState("error");
          setResult({ error: known ? { key: known } : serverMessage ? { message: serverMessage } : { key: "resumeFailed" } });
        }
      } catch (err: unknown) {
        console.error("Login failed:", err);
        setState("error");
        setResult({ error: { key: "unexpected" } });
      }
    };

    doLogin();
  }, [stateParam]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fafafa" }}>
      <div style={{ width: 420, textAlign: "center" }}>
        <div style={{ marginBottom: 40 }}>
          <span style={{ fontSize: 48, fontWeight: 700, fontStyle: "italic", fontFamily: '"Amazon Ember Thin", sans-serif', background: "linear-gradient(to right, #8741D0, #07C79D)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
            {t("login.brand")}
          </span>
        </div>

        <Container header={<Header variant="h2">{state === "loading" ? t("login.authenticating") : state === "resuming" ? t("login.resuming") : state === "success" ? t("login.welcome") : t("login.failed")}</Header>}>
          <SpaceBetween size="m">
            {state === "loading" && (
              <Box textAlign="center" padding={{ vertical: "l" }}>
                <StatusIndicator type="loading">{t("login.signingIn")}</StatusIndicator>
              </Box>
            )}

            {state === "resuming" && (
              <Box textAlign="center" padding={{ vertical: "l" }}>
                <SpaceBetween size="s">
                  <StatusIndicator type="success">{t("login.signedIn")}</StatusIndicator>
                  <StatusIndicator type="loading">{t("login.resumingSession")}</StatusIndicator>
                </SpaceBetween>
              </Box>
            )}

            {state === "success" && (
              <Box textAlign="center" padding={{ vertical: "l" }}>
                <SpaceBetween size="s">
                  <StatusIndicator type="success">
                    {result.userId ? <span><SafeTrans t={t} i18nKey="login.signedInAs" values={{ user: result.userId }} components={{ strong: <strong /> }} /></span> : t("login.success")}
                  </StatusIndicator>
                  <Box color="text-body-secondary" fontSize="body-s">{t("login.canClose")}</Box>
                </SpaceBetween>
              </Box>
            )}

            {state === "error" && (
              <Box textAlign="center" padding={{ vertical: "l" }}>
                <SpaceBetween size="m">
                  <StatusIndicator type="error">{result.error && ("message" in result.error ? result.error.message : t(`login.errors.${result.error.key}`))}</StatusIndicator>
                  <Button onClick={() => window.location.reload()}>{t("login.tryAgain")}</Button>
                </SpaceBetween>
              </Box>
            )}
          </SpaceBetween>
        </Container>

        <Box textAlign="center" padding={{ top: "l" }} color="text-body-secondary" fontSize="body-s">
          {t("login.footer")}
        </Box>
      </div>
    </div>
  );
}
